import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import UploadIcon from 'assets/ibanera/Icon_Upload.png';
import RedX from 'assets/ibanera/Icon_RedCross.png';
import Button from 'components/button/Button';
import { ErrorM } from 'components/form/ErrorM';

interface Props {
    fieldName: string;
    displayError?: boolean;
    multi?: boolean;
}

const UploadField: React.FC<Props> = ({ fieldName, displayError = true, multi = false }) => {
    const [, meta, helpers] = useField<File[] | File | null>(fieldName);
    const { setFieldTouched } = useFormikContext<any>();
    const [errorMessage, setErrorMessage] = useState('');
    const [storedFiles, setStoredFiles] = useState<File[]>([]);

    const onDrop = (accepted: File[], rejected: any[]) => {
        if (!accepted.length && rejected.length) {
            const reason = rejected[0].errors[0]?.message || 'Invalid file';
            setErrorMessage(reason);
            return;
        }

        setErrorMessage('');
        setFieldTouched(fieldName, true);

        const filesToSet = multi ? [...storedFiles, ...accepted] : accepted;

        setStoredFiles(filesToSet);
        helpers.setValue(multi ? filesToSet : filesToSet[0]);
    };

    const onDropRejected = (rejections: any[]) => {
        if (rejections.length > 0) {
            const firstError = rejections[0].errors[0];
            if (firstError?.code === 'file-invalid-type') {
                setErrorMessage('Invalid file type. Please upload PDF files only.');
            } else if (firstError?.code === 'file-too-large') {
                setErrorMessage('File size must be less than 10MB');
            } else if (firstError?.message) {
                setErrorMessage(firstError.message);
            } else {
                setErrorMessage('File rejected. Please ensure the file is PDF and less than 10MB.');
            }
        }
    };

    const maxSize = 10 * 1024 * 1024;
    const { getRootProps, getInputProps, open } = useDropzone({
        multiple: multi,
        accept: '.pdf',
        maxSize: maxSize,
        onDrop,
        onDropRejected,
        validator: (file) => {
            if (file.size > maxSize) {
                return {
                    message: `File size must be less than 10MB`,
                } as any;
            }
            return null;
        },
    });

    const deleteFile = (fileName: string) => {
        const updated = storedFiles.filter((file) => file.name !== fileName);
        setStoredFiles(updated);
        helpers.setValue(multi ? updated : null);
    };

    useEffect(() => {
        const timer = errorMessage
            ? setTimeout(() => {
                  setErrorMessage('');
              }, 5000)
            : undefined;

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [errorMessage]);

    return (
        <div className="UploadDocumentModal">
            <div
                {...getRootProps({
                    className: `Dropzone ${meta.error && meta.touched ? 'ErrorInput' : ''}`,
                })}
                onBlur={() => setFieldTouched(fieldName, true)}
            >
                <input {...getInputProps()} />
                {!multi && storedFiles.length ? (
                    <span>{storedFiles[0].name}</span>
                ) : (
                    <>
                        <img src={UploadIcon} alt="upload" />
                        <h5>PDF file</h5>
                        <span>Click or drag to upload</span>
                    </>
                )}
            </div>

            <div className="DropzoneFooter">
                <span>Accepts - .pdf</span>
                {storedFiles.length > 0 && (
                    <Button
                        type="button"
                        priority="secondary"
                        className="ResetBtn"
                        onClick={() => {
                            setStoredFiles([]);
                            helpers.setValue(multi ? [] : null);
                        }}
                    >
                        Reset
                    </Button>
                )}
            </div>

            {displayError && <ErrorM name={fieldName} />}
            {errorMessage && <p className="ErrorText NoMargin">{errorMessage}</p>}

            {multi &&
                storedFiles.map((file, idx) => (
                    <div className="DocumentListContainer" key={file.name + idx}>
                        <p>{file.name}</p>
                        <img src={RedX} alt="delete" onClick={() => deleteFile(file.name)} />
                    </div>
                ))}
        </div>
    );
};

export default UploadField;
